import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const isSupabaseConfigured = () => {
  return (
    Boolean(supabaseUrl) &&
    Boolean(supabaseAnonKey) &&
    !supabaseUrl.includes('your-project-id') &&
    supabaseUrl.startsWith('https://')
  );
};

// Create the Supabase client (or a dummy client if not yet configured to prevent startup crashes)
export const supabase = isSupabaseConfigured()
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    })
  : createClient('https://placeholder.supabase.co', 'placeholder-key', {
      auth: { persistSession: false }
    });

/**
 * Perform a live connectivity and health check against Supabase.
 * Checks basic ping and accessibility of core tables.
 */
export const checkSupabaseConnection = async () => {
  if (!isSupabaseConfigured()) {
    return {
      connected: false,
      status: 'unconfigured',
      message: 'Supabase credentials missing. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file.',
      tables: {}
    };
  }

  const startTime = Date.now();
  const tableChecks = {};

  try {
    // Check tenants table
    const { error: tenantsError } = await supabase
      .from('tenants')
      .select('id')
      .limit(1);
    
    tableChecks.tenants = !tenantsError;

    // Check expenses table
    const { error: expensesError } = await supabase
      .from('expenses')
      .select('id')
      .limit(1);
    tableChecks.expenses = !expensesError;

    // Check invoices table
    const { error: invoicesError } = await supabase
      .from('invoices')
      .select('id')
      .limit(1);
    tableChecks.invoices = !invoicesError;

    // Check inventory table
    const { error: inventoryError } = await supabase
      .from('inventory')
      .select('id')
      .limit(1);
    tableChecks.inventory = !inventoryError;

    const latency = Date.now() - startTime;

    if (tenantsError && tenantsError.code === '42P01') {
      return {
        connected: true,
        status: 'schema_missing',
        latency,
        message: 'Connected to Supabase, but database tables are not yet created. Run supabase_schema.sql in the Supabase SQL editor.',
        tableChecks
      };
    }

    if (tenantsError) {
      return {
        connected: false,
        status: 'error',
        latency,
        message: `Database error: ${tenantsError.message}`,
        tableChecks
      };
    }

    return {
      connected: true,
      status: 'healthy',
      latency,
      message: `Successfully connected to Supabase (${latency}ms latency).`,
      tableChecks
    };
  } catch (err) {
    return {
      connected: false,
      status: 'error',
      message: `Connection failed: ${err.message || err}`,
      tableChecks
    };
  }
};
